import { useQuery } from "react-query";
import { z } from "zod";
import axios from "axios";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { PuckPage } from "./useCreatePuckPage";
import i18next from "i18next";
import { PuckPageResponseSchema } from "./useGetPuckPage";
import { useIsCmsEnabled } from "./useIsCmsEnabled";
import { useMemo, useRef } from "react";

export const getPuckPageByUrlQueryKey = "getPuckPageByUrl";

type GetPuckPageByUrlOptions = {
    url: string;
    selectedCompanyId?: string | number | null;
    selectedEntityId?: string | number | null;
    scoreId?: string | null;
    entityType?: string | null;
    enabled?: boolean;

    // https://foodpilot.atlassian.net/browse/PF-843
    // It has been asked to remake this call on campaign change, due to Superset Dashboard
    campaignId?: number;
};

export const useGetPuckPageByUrl = (options: GetPuckPageByUrlOptions) => {
    const validatedOptions = GetPuckPageByUrlOptionsSchema.parse(options);
    const { url, enabled = true, selectedCompanyId, selectedEntityId, scoreId, entityType } = validatedOptions;
    const lang = i18next.language;
    const isCmsEnabled = useIsCmsEnabled();

    const hasSupersetDashboard = useRef<boolean | null>(null);
    const campaignId =
        hasSupersetDashboard.current === false || hasSupersetDashboard.current === null ?
            undefined
        :   options.campaignId;

    const endpoint = `/api/cms/puck/page-by-url/${url}`;

    const query = useQuery({
        queryKey: [
            getPuckPageByUrlQueryKey,
            url,
            selectedCompanyId ?? null,
            selectedEntityId ?? null,
            scoreId ?? null,
            entityType ?? null,
            lang,
            campaignId,
        ],
        enabled: enabled && url !== "" && isCmsEnabled,
        queryFn: async () => {
            const baseParams: Record<string, any> = {};

            const entityId = selectedEntityId ?? selectedCompanyId;
            if (entityId !== null && entityId !== undefined && String(entityId) !== "") {
                baseParams.selected_entity_id = String(entityId);
            }

            if (selectedCompanyId !== null && selectedCompanyId !== undefined && String(selectedCompanyId) !== "") {
                baseParams.selected_organization_id = String(selectedCompanyId);
            }

            const withContextParams: Record<string, any> = { ...baseParams };

            if (typeof entityType === "string" && entityType.trim() !== "") {
                withContextParams.entity_type = entityType;
            }

            if (scoreId !== null && scoreId !== undefined && scoreId !== "") {
                withContextParams.score_id = scoreId;
            }

            const shouldTryFallback =
                typeof withContextParams.entity_type === "string" || typeof withContextParams.score_id === "string";

            try {
                const response = await loggedInApiCall.get<PuckPage>(endpoint, { params: withContextParams });
                return PuckPageResponseSchema.parse(response.data);
            } catch (error: unknown) {
                if (!shouldTryFallback) {
                    throw error;
                }

                if (axios.isAxiosError(error) && error.response?.status === 404) {
                    const response = await loggedInApiCall.get<PuckPage>(endpoint, { params: baseParams });
                    return PuckPageResponseSchema.parse(response.data);
                }

                throw error;
            }
        },
        onError: (error: unknown) => {
            if (error instanceof Error) {
                console.error("Error fetching Puck page by URL:", error.message);
            } else {
                console.error("Unknown error fetching Puck page by URL:", error);
            }
        },
    });

    const page = query.data;

    const puckContent = useMemo(() => {
        try {
            return page?.content ? JSON.parse(page.content) : {};
        } catch {
            return {};
        }
    }, [page?.content]);

    const hasOneSupersetElement = puckContent?.content?.some((item: any) => {
        const hasSuperset = item?.type.includes("Superset");
        return hasSuperset;
    });
    if (hasOneSupersetElement) {
        hasSupersetDashboard.current = true;
    }

    return {
        page,
        puckContent,
        ...query,
    };
};

export const GetPuckPageByUrlOptionsSchema: z.ZodSchema<GetPuckPageByUrlOptions> = z.object({
    url: z.string(),
    selectedCompanyId: z.union([z.string(), z.number()]).optional().nullable(),
    selectedEntityId: z.union([z.string(), z.number()]).optional().nullable(),
    scoreId: z.string().optional().nullable(),
    entityType: z.string().optional().nullable(),
    enabled: z.boolean().optional(),
});
